import React, { useState, useEffect } from "react";
import { Button, Table, Tag, Space, message, Radio, Input, Spin, notification } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { jobs, deleteJob, getJob } from "@/services/common";
import userInfo from "@/Components/user";
import dayjs from "dayjs";
import { SelectOutlined, PlusOutlined, BarChartOutlined } from "@ant-design/icons";
import { AppProvider, useAppContext } from "@/AppContext";

// 类型定义
interface TaskItem {
  id: number;
  name: string;
  input_data: string;
  status: string;
  created_at: string;
  updated_at: string;
  result_path: string;
  tags: string[];
  rowIndex: number;
  selectVisable?: boolean;
}

interface PcTaskItem {
  id: number;
  name: string;
  status: string;
  createTime: string;
  finishedTime: string;
}
const statusMap: any = {
  done: { color: "#28A745", label: "已完成" },
  processing: { color: "#007cdc", label: "计算中" },
  pending: { color: "#FAAD14", label: "排队中" },
  error: { color: "#FF4D4F", label: "计算失败" },
};

const TaskManagement = ({ projectId, flowId }: { projectId: string; flowId: string }) => {
  const [source, setSource] = useState<PcTaskItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredSource, setFilteredSource] = useState<PcTaskItem[]>([]); // 筛选后的数据
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [taskList, setTaskList] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [total, setTotal] = useState<number>(0);
  const [selectedTasks, setSelectedTasks] = useState<boolean>(false);
  const pageSize = 10;
  const { state, actions } = useAppContext();
  const [globalData, setGlobalData] = useState<any>(null);
  const [api, notificationContextHolder] = notification.useNotification();

  const fetchData = () => {
    setLoading(true);
    jobs(
      {
        page: currentPage,
        perPage: pageSize,
        flow_id: flowId,
      },
      userInfo.access_token,
    )
      .then(async (res) => {
        if (res.status === 200) {
          console.log(res);
          setTotal(res.data.count);
          // const filteredList = res.data.data.filter(
          //   (task) => task.name !== "hydraulics新计算任务"
          // );
          // setTaskList(filteredList);
          setTaskList(res.data.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  useEffect(() => {
    fetchData();
  }, [currentPage, flowId]);

  // console.log("filteredSource:", filteredSource);

  useEffect(() => {
    if (state.curFlow) {
      setGlobalData(state.curFlow.global_data);
    }
  }, [state.curFlow?.id]);

  const transformData = (mobileData: TaskItem[]): PcTaskItem[] => {
    return mobileData.map((item) => ({
      id: item.id,
      name: item.name,
      status: item.status,
      createTime: dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss"),
      finishedTime: dayjs(item.updated_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss"),
    }));
  };

  // 加载数据
  const loadData = () => {
    if (!taskList || taskList.length === 0) return;
    const transformed = transformData(taskList);
    setSource(transformed);
    setFilteredSource(transformed);
  };

  useEffect(() => {
    loadData();
  }, [taskList]);
  // 修复：只在 筛选状态 / 搜索关键词 变化时重置页码
  useEffect(() => {
    let result = [...source];
    if (searchKeyword) {
      result = result.filter((item) =>
        item.name.toLowerCase().includes(searchKeyword.toLowerCase()),
      );
    }
    if (filterStatus !== "all") {
      result = result.filter((item) => item.status === filterStatus);
    }
    setFilteredSource(result);

    // 只在 筛选/搜索 变化时重置到第1页
    // 不要监听 source，否则翻页取到新数据会触发重置
  }, [filterStatus, source, searchKeyword]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
    // 核心：只允许 已完成（done）的任务可以勾选
    getCheckboxProps: (record: PcTaskItem) => ({
      disabled: record.status !== "done", // 非 done 状态 → 禁用勾选
    }),
    // 核心修复：支持跨页选择，保留已选数据
    preserveSelectedRowKeys: true,
  };

  // 跳转至计算参数页面（带任务ID）
  const goToTaskParams = (taskId: number) => {
    if (projectId && flowId) {
      navigate(`/project/${projectId}/flow/${flowId}/taskParams/${taskId}`);
    } else {
      console.warn("缺少项目ID或流程ID，无法跳转到参数查看页面");
    }
  };
  const goToTaskDate = (taskId: number) => {
    if (projectId && flowId) {
      navigate(`/project/${projectId}/flow/${flowId}/taskDate/${taskId}`);
    } else {
      console.warn("缺少项目ID或流程ID，无法跳转到参数查看页面");
    }
  };
  const goToDesign = () => {
    if (!projectId || !flowId) {
      message.error("缺少项目ID或流程ID，无法跳转");
      console.warn("缺少项目ID或流程ID：", { projectId, flowId });
      return;
    }
    navigate(`/project/${projectId}/flow/${flowId}/params`, { state: { globalData } });
    // message.info("正在跳转至参数设置页面");
  };

  // 删除确认
  const del_confirm = (item: PcTaskItem) => {
    if (window.confirm(`确定要删除任务「${item.name}」吗？`)) {
      deleteJob(item.id, userInfo.access_token)
        .then((r) => {
          if (r.status === 200) {
            const newSource = source.filter((t) => t.id !== item.id);
            setSource(newSource);
            message.success("删除成功");
          } else message.error("删除失败");
        })
        .catch(() => message.error("删除请求异常"));
    }
  };

  // ====================== 核心：跳转对比分析页面 ======================
  const goToComparativeAnalysis = () => {
    if (selectedRowKeys.length < 2) {
      message.warning("请至少选择 2 个任务进行对比");
      return;
    }
    if (!projectId || !flowId) {
      message.error("缺少项目ID或流程ID");
      return;
    }

    // 拼接选中的任务ID，用逗号分隔
    const taskIds = selectedRowKeys.join(",");

    // 跳转到对比页面，并携带任务ID
    navigate(`/project/${projectId}/flow/${flowId}/comparativeAnalysis?taskIds=${taskIds}`);
  };

  const getJobDetail = (taskId: number) => {
    getJob(taskId, userInfo.access_token)
      .then((r) => {
        console.log("rrrrrrrr", r);
        api.warning({
          // key: notificationKey,
          message: <b>{r.data?.name || taskId} 计算错误详情：</b>,
          description:  JSON.parse(r.data?.output || "{}").error_type || "无错误信息",
          placement: "topRight",
          duration: 0,
          
        });
      })
      .catch(() => message.error("获取任务详情请求异常"));
  };

  // ==================================================================

  const columns:any = [
    {
      title: "任务名称",
      dataIndex: "name",
      key: "name",
      align: "center",
      width: "20%",
      render: (text: string, record: PcTaskItem) => {
        const canClick = record.status === "done";
        const showText = `${text} (${record.id})`;
        if (canClick) {
          return (
            <a onClick={() => goToTaskDate(record.id)} style={{ cursor: "pointer" }}>
              {showText}
            </a>
          );
        }
        return <span style={{ color: "#666" }}>{showText}</span>;
      },
    },
    {
      title: "计算状态",
      dataIndex: "status",
      align: "center",
      key: "status",
      width: "12%",
      render: (status: string) => (
        <Tag color={statusMap[status]?.color || "orange"}>{statusMap[status]?.label || status}</Tag>
      ),
    },
    {
      title: "创建日期",
      align: "center",
      dataIndex: "createTime",
      key: "createTime",
      width: "20%",
    },
    {
      title: "完成时间",
      dataIndex: "finishedTime",
      align: "center",
      key: "finishedTime",
      width: "20%",
      render: (t: string, record: PcTaskItem) => (
        <div>{record.status === "done" || record.status === "error" ? t : "--"}</div>
      ),
    },
    {
      title: "计算参数",
      key: "params",
      align: "center",
      width: "13%",
      render: (_: any, record: PcTaskItem) => (
        <Button type="primary" onClick={() => goToTaskParams(record.id)}>
          查看计算参数
        </Button>
      ),
    },
    // {
    //   title: '设置参数',
    //   key: 'setting',
    //   width: '10%',
    //   render: (_: any) => (
    //     <Button type="primary" onClick={goToDesign}>
    //       设置参数
    //     </Button>
    //   ),
    // },
    {
      title: "操作",
      key: "action",
      align: "center",
      width: "10%",
      render: (_, record: PcTaskItem) => (
        <Space size="middle">
          <Button
            type="link"
            icon={
              <EyeOutlined
                style={{
                  color:
                    record.status === "done" ? "blue" : record.status === "error" ? "red" : "gray",
                }}
              />
            }
            disabled={record.status === "done" || record.status === "error" ? false : true}
            onClick={() => {
              if (record.status === "done") {
                goToTaskDate(record.id);
              } else {
                getJobDetail(record.id);
              }
            }}
            size="small"
          >
            {record.status === "done" ? (
              "查看结果"
            ) : record.status === "error" ? (
              <span style={{ color: "red" }}>错误信息</span>
            ) : (
              "等待计算"
            )}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => del_confirm(record)}
            size="small"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px", backgroundColor: "#fff" }}>
      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
        }}
      >
        {" "}
        {/* 修改：flex布局实现搜索框和筛选并排 */}
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px" }}>
          <Button type="primary" onClick={goToDesign}>
            <PlusOutlined />
            新建计算任务
          </Button>
          {!selectedTasks ? null : (
            <Button
              disabled={selectedRowKeys.length < 2}
              onClick={goToComparativeAnalysis}
              color="orange"
              variant="solid"
            >
              <BarChartOutlined />
              查看对比分析
            </Button>
          )}
          <Button onClick={() => setSelectedTasks(!selectedTasks)}>
            <SelectOutlined />
            {selectedTasks ? "取消" : "批量处理"}
          </Button>
        </div>
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "16px" }}>
          <Input
            placeholder="请输入任务名称搜索"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 240 }}
            allowClear // 新增：清空按钮
          />
          <Radio.Group
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">全部</Radio.Button>
            <Radio.Button value="done">已完成</Radio.Button>
            <Radio.Button value="processing">计算中</Radio.Button>
            <Radio.Button value="pending">排队中</Radio.Button>
            <Radio.Button value="error">计算失败</Radio.Button>
          </Radio.Group>
        </div>
      </div>
      {notificationContextHolder}
      <Table
        rowSelection={selectedTasks ? rowSelection : null}
        // rowSelection={null}
        columns={columns}
        dataSource={loading ? [] : filteredSource}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: loading ? "" : "暂无数据" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          onChange: (page) => setCurrentPage(page),
          showSizeChanger: false,
        }}
        bordered
        scroll={{ x: 900 }}
        style={{ textAlign: "center" }}
      />
    </div>
  );
};

export default TaskManagement;
